import { FC, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { RelativeTime } from '@bonusly/common-relative-time';
import { Box, EyeOutline, Flex, Spacer, StyleProps, Text } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Feedback } from '../../lib';
import { FeedbackState } from '../../types/feedback';
import { useFeedbackContext } from '../context/feedback-context';

interface EvaluationHeaderProps extends StyleProps {
	evaluation: Feedback;
	isNew: boolean;
	createdAt: Date;
}

export const UnrequestedFeedbackHeader: FC<EvaluationHeaderProps> = ({ evaluation, isNew = false, createdAt }) => {
	const { scope } = useFeedbackContext();
	const { t } = useTranslation('feedback');
	const user = useCurrentUser();

	const name = user.id == evaluation.receiverId ? evaluation.giver?.name : evaluation.receiver.name;

	const translationKey = useMemo(() => {
		if (scope == 'manager') {
			return 'feedback.unrequested.they_received';
		} else if (evaluation.receiverId == user.id) {
			return 'feedback.unrequested.you_received';
		} else if (evaluation.giverId == user.id && evaluation.state == FeedbackState.Draft) {
			return 'feedback.unrequested.you_draft';
		} else if (evaluation.giverId == user.id) {
			return 'feedback.unrequested.you_gave';
		}
	}, [evaluation, user, scope]);

	return (
		<Flex bg="bg.canvas" borderRadius="md" p={3} mb={2}>
			<Text fontWeight="semibold">
				<Trans
					t={t}
					i18nKey={translationKey}
					values={{ name }}
					components={[
						<Text key={evaluation.id} as="span" fontWeight="normal">
							got feedback from
						</Text>,
					]}
				/>
			</Text>
			<Spacer />

			<Flex alignItems="center">
				<Text size="sm" color="text.softest">
					<Trans i18nKey="request.active_time" t={t}>
						Active
						<RelativeTime color="text.softest" date={createdAt} fontSize="sm" />
						ago
					</Trans>
				</Text>

				{isNew && <Box data-testid="pending-badge" boxSize={2} borderRadius="full" bg="red.400" ml={2} />}
			</Flex>

			<Flex alignItems="center">
				<Box color="gray.700" fontSize="medium" px={1} py="2px">
					|
				</Box>
				<EyeOutline size="xs" mr={1} />
				<Text size="sm" color="gray.700" display="inline">
					{t('visibility.shared')}
				</Text>
			</Flex>
		</Flex>
	);
};
